import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiUrl } from "@/lib/api-client";
import { enqueueCrmSyncOp } from "@/lib/crm-sync-outbox";
import { deleteMeasurementWizardDraft } from "@/lib/measurement-wizard-draft";
import { notify } from "@/lib/notify";
import { getMeasureCompleteTemplateStageId } from "../../functions/lib/settings";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { MeasurementWizard } from "@/components/measurement/MeasurementWizard";
import { ContactSelectCombobox } from "@/components/crm/ContactSelectCombobox";
import { preventDismissWhenPointerOnRadixPopper } from "@/lib/radix-outside-popover-guard";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  buildMeasurementAddress,
  reverseGeocodeBest,
} from "@/lib/measurement-location";

export default function MeasurePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [createOpen, setCreateOpen] = useState(false);
  /** Popover portál célja — modal Dialog nem tiltja a kontaktlista görgetését. */
  const [createDealDialogHost, setCreateDealDialogHost] = useState<HTMLElement | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [contactId, setContactId] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactCity, setNewContactCity] = useState("");
  const [newContactStreet, setNewContactStreet] = useState("");
  const [newContactIsCompany, setNewContactIsCompany] = useState(false);
  const [newContactIco, setNewContactIco] = useState("");

  const appSettings = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => {
      try {
        const url = apiUrl("/api/settings");
        const r = await Promise.race([
          fetch(url),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 8000)),
        ]) as Response;
        if (!r.ok) throw new Error(`Settings fetch error: ${r.status}`);
        const data = await r.json();
        return data;
      } catch (err) {
        console.warn("Settings fetch failed, using defaults:", err instanceof Error ? err.message : err);
        return { manualsFolderId: null, measureTargetStageId: null };
      }
    },
    retry: 0,
    staleTime: 120000,
  });

  const board = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/board"), { credentials: "include" });
      if (!r.ok) throw new Error(`Board fetch error: ${r.status}`);
      return r.json();
    },
  });

  const contacts = useQuery({
    queryKey: queryKeys.crmContacts,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/contacts"), { credentials: "include" });
      if (!r.ok) throw new Error(`Contacts fetch error: ${r.status}`);
      const j = await r.json();
      return j.contacts as any[];
    },
  });

  const measureStages = useMemo(() => {
    const stages = board.data?.stages || [];
    return stages.filter((s: any) => {
      const measure = s.measure === true || s.measure === "true";
      const nameMatch = (s.name || "").toLowerCase().includes("ki kell mennünk személyesen");
      return measure || nameMatch;
    });
  }, [board.data]);

  const createContact = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl("/api/crm/contacts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: newContactName,
          email: newContactEmail || null,
          phone: newContactPhone || null,
          city: newContactCity || null,
          street: newContactStreet || null,
          isCompany: newContactIsCompany,
          ico: newContactIco || null,
        }),
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({}));
        const message =
          error?.userMessage ||
          error?.details?.message ||
          error?.message ||
          `${t("measure.contactCreateFailed")}: ${r.status}`;
        throw new Error(message);
      }
      return r.json();
    },
    onSuccess: (created) => {
      qc.setQueryData(queryKeys.crmContacts, (prev: any) => {
        const existing = Array.isArray(prev) ? prev : [];
        if (created?.id && !existing.some((c: any) => c.id === created.id)) {
          return [created, ...existing];
        }
        return existing;
      });
      qc.invalidateQueries({ queryKey: queryKeys.crmContacts });
    },
  });

  const createDeal = useMutation({
    mutationFn: async (payload: { title: string; contactId: string; stageId?: string; closingDate?: string }) => {
      const r = await fetch(apiUrl("/api/crm/deals"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({}));
        const message =
          error?.userMessage ||
          error?.details?.message ||
          error?.message ||
          `Deal létrehozása sikertelen: ${r.status}`;
        throw new Error(message);
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["measure-deals"] });
      qc.refetchQueries({ queryKey: ["measure-deals"], type: "active" });
    },
  });

  const handleCreateDeal = async () => {
    try {
      let finalContactId = contactId;
      let title = newTitle.trim();

      if (contactId === "new") {
        if (!newContactName.trim()) {
          alert(t("measure.contactRequired"));
          return;
        }
        const created = await createContact.mutateAsync();
        finalContactId = created?.id;
        if (created?.id) {
          setContactId(created.id);
        }
      }

      if (!finalContactId) {
        alert(t("measure.contactSelectRequired"));
        return;
      }

      if (!title) {
        title =
          contactId === "new"
            ? newContactName.trim()
            : (contacts.data || []).find((c: any) => c.id === finalContactId)?.name || t("measure.newDeal");
      }

      const stages = measureStages.length > 0 ? measureStages : (board.data?.stages || []);
      const defaultStageId =
        stages.find((s: any) => (s.name || "").includes("Új megkeresés"))?.id ||
        stages[0]?.id;

      const createdDeal = await createDeal.mutateAsync({
        title,
        contactId: finalContactId,
        stageId: defaultStageId || undefined,
        closingDate: new Date().toISOString().slice(0, 10),
      });

      setCreateOpen(false);
      setNewTitle("");
      setContactId("");
      setNewContactName("");
      setNewContactEmail("");
      setNewContactPhone("");
      setNewContactCity("");
      setNewContactStreet("");
      setNewContactIsCompany(false);
      setNewContactIco("");

      if (createdDeal?.id) {
        navigate(`/crm/measure/${createdDeal.id}`, { state: { fromCreate: true } });
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : t("measure.createError"));
    }
  };

  const [lastMeasurementData, setLastMeasurementData] = useState<any>(null);

  const finishMeasurement = async (localMeasurementData?: any) => {
    if (!id) {
      alert(t("measure.noDealSelected"));
      return;
    }

    const md = localMeasurementData ?? lastMeasurementData;

    // --- Geolocation + reverse (GPS is source of truth; address may be coarse) ---
    let geoLat: number | null = null;
    let geoLng: number | null = null;
    let address: string | null = null;

    if (navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error("TIMEOUT"));
          }, 15000);
          navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(timeoutId); resolve(pos); },
            (error) => { clearTimeout(timeoutId); reject(error); },
            { timeout: 15000, maximumAge: 0, enableHighAccuracy: true },
          );
        });
        geoLat = position.coords.latitude;
        geoLng = position.coords.longitude;
        try {
          const googleKey =
            (import.meta.env as any).VITE_GOOGLE_MAPS_API_KEY ||
            (import.meta.env as any).VITE_GOOGLE_MAPS_KEY ||
            "";
          const reverse = await reverseGeocodeBest(geoLat, geoLng, {
            googleApiKey: googleKey || null,
          });
          address = buildMeasurementAddress(reverse, geoLat, geoLng).address;
        } catch {
          address = buildMeasurementAddress(null, geoLat, geoLng).address;
        }
      } catch (geoErr) {
        if (geoErr instanceof GeolocationPositionError) {
          switch (geoErr.code) {
            case geoErr.PERMISSION_DENIED:
              alert(t("measure.geolocationDenied"));
              return;
            case geoErr.POSITION_UNAVAILABLE:
              break;
            case geoErr.TIMEOUT:
              break;
          }
        }
      }
    }

    // --- Build payloads ---
    const geoPatch: Record<string, unknown> = {};
    if (geoLat != null && geoLng != null) {
      geoPatch.geoLat = geoLat;
      geoPatch.geoLng = geoLng;
      if (address) geoPatch.address = address;
    }

    const mdPatch: Record<string, unknown> = {};
    if (md) {
      mdPatch.measurementData = { ...md, completedAt: new Date().toISOString() };
    } else {
      mdPatch.measurementData = { completedAt: new Date().toISOString() };
    }

    const combinedPatch = { ...geoPatch, ...mdPatch };

    // --- Send or queue ---
    let sentOk = false;
    try {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(combinedPatch),
      });
      sentOk = r.ok;
    } catch {
      sentOk = false;
    }

    if (!sentOk) {
      await enqueueCrmSyncOp("deal_patch", id, combinedPatch);
    }

    // --- Stage move ---
    const settingsData = appSettings.data as any;
    let dealPipe = "";
    try {
      const cached = qc.getQueryData<any>(["deal", id]);
      dealPipe = String(cached?.pipeline_id ?? cached?.pipelineId ?? "");
    } catch { /* ignore */ }

    const target = getMeasureCompleteTemplateStageId(
      settingsData?.automations?.onMeasureComplete,
      dealPipe || null,
      settingsData?.measureTargetStageId ?? null,
    );
    if (target) {
      let stageOk = false;
      try {
        const r = await fetch(apiUrl(`/api/crm/deals/${id}/stage?_=${Date.now()}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ stageId: target, keepSubpipeline: true }),
        });
        stageOk = r.ok;
      } catch {
        stageOk = false;
      }
      if (!stageOk) {
        await enqueueCrmSyncOp("deal_stage", id, { stageId: target, keepSubpipeline: true });
      }
    }

    // --- Cleanup ---
    void deleteMeasurementWizardDraft(id);
    qc.invalidateQueries({ queryKey: ["deal", id] });

    if (!sentOk) {
      notify.info(t("uploadOutbox.patchQueued"), t("uploadOutbox.patchQueuedDesc"));
    }
    navigate("/crm");
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header: on mobile the step indicator is portaled here by MeasurementWizard */}
      <div
        id="measure-step-header"
        className={cn(
          "flex-shrink-0 bg-background border-b z-20",
          isMobile ? "min-h-0" : "h-[60px] px-4 py-4 relative overflow-hidden"
        )}
      />

      {/* Wizard */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <MeasurementWizard
          dealId={id || null}
          onDealSelect={(dealId) => {
            navigate(`/crm/measure/${dealId}`);
          }}
          onComplete={(md) => { setLastMeasurementData(md); void finishMeasurement(md); }}
          onCreateDeal={() => setCreateOpen(true)}
          initialStepFromCreate={location.state?.fromCreate === true}
        />
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          ref={setCreateDealDialogHost}
          className="sm:max-w-md"
          onPointerDownOutside={preventDismissWhenPointerOnRadixPopper}
          onInteractOutside={preventDismissWhenPointerOnRadixPopper}
          onFocusOutside={preventDismissWhenPointerOnRadixPopper}
        >
          <DialogHeader>
            <DialogTitle>{t("measure.createDealTitle")}</DialogTitle>
            <DialogDescription>
              {t("measure.createDealDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label htmlFor="measure-create-contact" className="block text-sm text-foreground/70 mb-1">
                {t("measure.contact")} <span className="text-destructive">*</span>
              </label>
              <ContactSelectCombobox
                id="measure-create-contact"
                portalContainer={createDealDialogHost}
                value={contactId}
                onValueChange={setContactId}
                contacts={(contacts.data || []) as { id: string; name?: string | null; email?: string | null; phone?: string | null }[]}
                disabled={contacts.isLoading}
                loading={contacts.isLoading}
                loadingLabel={t("crm.loadingContacts")}
                noneLabel={t("measure.none")}
                newContactLabel={t("measure.newContact")}
                searchPlaceholder={t("deal.searchContacts")}
                emptyLabel={t("deal.noResults")}
              />
            </div>
            {contactId === "new" && (
              <div className="grid grid-cols-1 gap-2">
                <Input
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder={t("deal.name") + " *"}
                />
                <Input
                  value={newContactEmail}
                  onChange={(e) => setNewContactEmail(e.target.value)}
                  placeholder={t("deal.email")}
                />
                <Input
                  value={newContactPhone}
                  onChange={(e) => setNewContactPhone(e.target.value)}
                  placeholder={t("deal.phone")}
                />
                <Input
                  value={newContactCity}
                  onChange={(e) => setNewContactCity(e.target.value)}
                  placeholder={t("deal.city")}
                />
                <Input
                  value={newContactStreet}
                  onChange={(e) => setNewContactStreet(e.target.value)}
                  placeholder={t("deal.street")}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newContactIsCompany}
                    onChange={(e) => setNewContactIsCompany(e.target.checked)}
                    className="rounded border"
                  />
                  {t("deal.company")}
                </label>
                {newContactIsCompany && (
                  <Input
                    value={newContactIco}
                    onChange={(e) => setNewContactIco(e.target.value)}
                    placeholder={t("measureExtra.icoPlaceholder")}
                  />
                )}
              </div>
            )}
            <div>
              <label className="block text-sm text-foreground/70 mb-1">
                {t("measure.dealTitle")}
              </label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t("measure.dealTitlePlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleCreateDeal}
              disabled={createDeal.isPending || createContact.isPending}
            >
              {createDeal.isPending || createContact.isPending ? t("measure.creating") : t("measure.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
